import React, { useEffect, useMemo, useState } from 'react';
import {
  Save,
  X,
  Plus,
  Trash2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Upload,
  ListChecks
} from 'lucide-react';
import toast from 'react-hot-toast';
import { claimsAPI } from '../services/apiService';
import { sendWhatsAppMessage } from '../services/whatsappTemplates';
import QuotePreview from './QuotePreview';
import ServiceHistoryImportModal from './ServiceHistoryImportModal';
import {
  CASE_FACTORS,
  calculatePricing,
  emptyFactorValues,
  requirementsToFactorDefaults,
  calculateFinalStatus,
  REQUIREMENT_STATUS_FIELD,
  FACTOR_STATUS_FIELD
} from '../services/pricingService';
import { canUseFeeTimelineCalculator, getStoredStaffUser } from '../utils/roles';
import {
  calculateServiceYears,
  computeContributionBalances,
  normalizeServiceHistoryEntry,
  sortServiceHistory,
  toTitleCaseWords,
  countTransfersNeededFromHistory,
  formatTotalServiceLabel
} from '../utils/serviceHistoryUtils';
import {
  INSTANCE_TRACKED_FACTOR_KEYS,
  emptyFactorInstances,
  isInstanceTrackedFactor,
  serviceHistoryRowKey,
  syncFactorInstancesForKey,
  toggleInstanceSelection,
  rollupInstanceStatuses
} from '../utils/factorInstances';

const STEPS = [
  { id: 'history', label: 'Service History' },
  { id: 'factors', label: 'Case Factors' },
  { id: 'fees', label: 'Fees & Payments' },
  { id: 'review', label: 'Review & Quote' }
];

const MODAL_HEIGHT = 'min(900px, 94vh)';
const MODAL_WIDTH = 'min(1480px, 98vw)';

const formatInr = (value) =>
  `₹${(parseFloat(value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const parseJsonField = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const blankHistory = () => ({
        company_name: '',
        member_id: '',
        doj: '',
        dol: '',
        service: '',
        pf_epf_balance: '',
        eps_balance: '',
        form_31: '',
        form_19: 'No',
        form_10c_scheme_certificate: 'No',
        form_13: 'NA'
});

const UpdateClaimStatus = ({ mode = 'update', claim, member, onClose, onSuccess }) => {
  const isCreate = mode === 'create' || !claim?.id;
  const canUsePricing = canUseFeeTimelineCalculator(getStoredStaffUser());

  const initialRequirements = Array.isArray(claim?.requirements)
    ? claim.requirements
    : (claim?.requirement ? claim.requirement.split(',').map((r) => r.trim()).filter(Boolean) : []);

  const initialPricingData = parseJsonField(claim?.pricing_data, {}) || {};
  const initialServiceHistory = (() => {
    const raw = parseJsonField(claim?.service_history, []);
    if (!Array.isArray(raw) || !raw.length) return [];
    return sortServiceHistory(
      raw.map((entry) => normalizeServiceHistoryEntry({
        ...blankHistory(),
        ...entry
      }))
    );
  })();

  let initialPayments = parseJsonField(claim?.payments, []);
  if (!Array.isArray(initialPayments)) initialPayments = [];
  if (initialPayments.length === 0 && claim?.advance) {
    initialPayments = [{
      amount: claim.advance,
      date: claim.updated_at ? new Date(claim.updated_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    }];
  }

  const seededFactors = requirementsToFactorDefaults(
    initialRequirements,
    {
      ...emptyFactorValues(),
      ...(initialPricingData.factorValues || {}),
      ...(initialPricingData.complexity || {})
    }
  );

  const statusSeed = {};
  Object.values(REQUIREMENT_STATUS_FIELD).forEach((field) => {
    statusSeed[field] = claim?.[field] || 'Not Started';
  });

  const [step, setStep] = useState(0);
  const [factorValues, setFactorValues] = useState(seededFactors);
  const [factorInstances, setFactorInstances] = useState(() => {
    const seeded = { ...emptyFactorInstances(), ...(initialPricingData.factorInstances || {}) };
    INSTANCE_TRACKED_FACTOR_KEYS.forEach((key) => {
      if (!Array.isArray(seeded[key])) seeded[key] = [];
    });
    return seeded;
  });
  const [serviceHistory, setServiceHistory] = useState(initialServiceHistory);
  const [payments, setPayments] = useState(initialPayments);
  const [finalAgreedFee, setFinalAgreedFee] = useState(
    claim?.fees || initialPricingData.finalAgreedFee || ''
  );
  const [initialAdvance, setInitialAdvance] = useState(
    initialPricingData.initialAdvance ?? ''
  );
  const [advanceManual, setAdvanceManual] = useState(
    initialPricingData.initialAdvance !== undefined
      && initialPricingData.initialAdvance !== null
      && initialPricingData.initialAdvance !== ''
  );
  const [remarks, setRemarks] = useState(claim?.remarks || '');
  const [statuses] = useState(statusSeed);
  const [saving, setSaving] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [instancePickerFactorKey, setInstancePickerFactorKey] = useState(null);
  const [claimRecordId, setClaimRecordId] = useState(claim?.id || null);
  /** When true, footer shows Save; after save it flips to Next until the step is edited again */
  const [stepNeedsSave, setStepNeedsSave] = useState(Boolean(isCreate));

  const markStepDirty = () => setStepNeedsSave(true);

  const contributionBalances = useMemo(
    () => computeContributionBalances(serviceHistory),
    [serviceHistory]
  );

  const pfBalanceForPricing = useMemo(() => {
    if (serviceHistory.length > 0) return contributionBalances.totalBalance;
    return (parseFloat(claim?.pf_balance) || 0)
      + (parseFloat(claim?.eps_balance) || 0)
      + (parseFloat(claim?.total_balance) || 0);
  }, [serviceHistory, contributionBalances, claim]);

  const pricing = useMemo(() => calculatePricing({
    factorValues,
    pfBalance: pfBalanceForPricing,
    finalAgreedFee: finalAgreedFee === '' ? null : finalAgreedFee,
    initialAdvance: advanceManual ? initialAdvance : null
  }), [factorValues, pfBalanceForPricing, finalAgreedFee, initialAdvance, advanceManual]);

  useEffect(() => {
    if (!advanceManual) {
      setInitialAdvance(pricing.defaultAdvance || '');
    }
  }, [pricing.defaultAdvance, advanceManual]);

  // Form-13 = No → auto-fill Case Factors Transfers (No.) + map those employment IDs
  useEffect(() => {
    const hasForm13Choice = serviceHistory.some((row) =>
      ['yes', 'no'].includes(String(row.form_13 || '').trim().toLowerCase())
    );
    // Legacy rows without Form-13 answers: do not overwrite existing Transfers count
    if (!hasForm13Choice) return;

    const transferCount = countTransfersNeededFromHistory(serviceHistory);
    const selectedKeys = serviceHistory
      .map((row, index) => ({ row, key: serviceHistoryRowKey(row, index) }))
      .filter(({ row }) => String(row.form_13 || '').trim().toLowerCase() === 'no')
      .map(({ key }) => key);

    setFactorValues((prev) => {
      if ((parseFloat(prev.transfers) || 0) === transferCount) return prev;
      return { ...prev, transfers: transferCount };
    });

    setFactorInstances((prev) => {
      const nextList = syncFactorInstancesForKey({
        count: transferCount,
        selectedKeys,
        serviceHistory,
        previousInstances: prev.transfers || []
      });
      const prevKeys = (prev.transfers || []).map((i) => i.key).join('|');
      const nextKeys = nextList.map((i) => i.key).join('|');
      if (prevKeys === nextKeys && (prev.transfers || []).length === nextList.length) {
        return prev;
      }
      return { ...prev, transfers: nextList };
    });
  }, [serviceHistory]);

  const requirements = pricing.requirements;
  const finalStatus = useMemo(
    () => calculateFinalStatus(requirements, statuses),
    [requirements, statuses]
  );

  const setFactor = (key, raw) => {
    if (key === 'transfers') return; // locked — driven by Form-13 = No
    markStepDirty();
    const nextCount = Math.max(0, parseFloat(raw) || 0);
    setFactorValues((prev) => ({ ...prev, [key]: nextCount }));
    if (isInstanceTrackedFactor(key)) {
      setFactorInstances((prev) => {
        if (nextCount <= 0) {
          return { ...prev, [key]: [] };
        }
        const selectedKeys = (prev[key] || []).map((i) => i.key).slice(0, nextCount);
        return {
          ...prev,
          [key]: syncFactorInstancesForKey({
            count: nextCount,
            selectedKeys,
            serviceHistory,
            previousInstances: prev[key] || []
          })
        };
      });
    }
  };

  const handleToggleFactorInstance = (factorKey, rowKey) => {
    markStepDirty();
    const maxCount = Math.max(0, parseFloat(factorValues[factorKey]) || 0);
    setFactorInstances((prev) => {
      const selectedKeys = toggleInstanceSelection(
        (prev[factorKey] || []).map((i) => i.key),
        rowKey,
        maxCount
      );
      return {
        ...prev,
        [factorKey]: syncFactorInstancesForKey({
          count: maxCount,
          selectedKeys,
          serviceHistory,
          previousInstances: prev[factorKey] || []
        })
      };
    });
  };

  const validateFactorInstances = () => {
    for (const key of INSTANCE_TRACKED_FACTOR_KEYS) {
      if (key === 'transfers') continue; // auto-mapped from Form-13
      const count = Math.max(0, parseFloat(factorValues[key]) || 0);
      if (count <= 1) continue;
      const selected = factorInstances[key] || [];
      if (selected.length !== count) {
        const label = CASE_FACTORS.find((f) => f.key === key)?.label || key;
        toast.error(`Select ${count} Service History row(s) for ${label}`);
        return false;
      }
    }
    if (
      INSTANCE_TRACKED_FACTOR_KEYS.some(
        (key) => key !== 'transfers' && (parseFloat(factorValues[key]) || 0) > 1
      )
      && serviceHistory.length === 0
    ) {
      toast.error('Add Service History first to map instance tracking');
      return false;
    }
    return true;
  };

  const handleServiceHistoryChange = (index, field, value) => {
    markStepDirty();
    setServiceHistory((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'doj' || field === 'dol') {
        const doj = field === 'doj' ? value : next[index].doj;
        const dol = field === 'dol' ? value : next[index].dol;
        next[index].service = calculateServiceYears(doj, dol);
        return sortServiceHistory(next);
      }
      if (field === 'company_name') {
        next[index].company_name = value;
      }
      return next;
    });
  };

  const handleCompanyBlur = (index) => {
    setServiceHistory((prev) => {
      const next = [...prev];
      const titled = toTitleCaseWords(next[index].company_name);
      if (titled !== next[index].company_name) markStepDirty();
      next[index] = {
        ...next[index],
        company_name: titled
      };
      return next;
    });
  };

  const buildPayload = () => {
    const balances = computeContributionBalances(serviceHistory);
    const totalPfEpfBalance = balances.pfBalance;
    const totalEpsBalance = balances.epsBalance;
    const fees = parseFloat(finalAgreedFee) || 0;
    const totalPayments = payments.reduce(
      (sum, payment) => sum + (parseFloat(payment.amount) || 0),
      0
    );

    const normalizedInstances = { ...emptyFactorInstances() };
    const statusFromInstances = { ...statuses };
    INSTANCE_TRACKED_FACTOR_KEYS.forEach((key) => {
      const count = Math.max(0, parseFloat(factorValues[key]) || 0);
      const synced = syncFactorInstancesForKey({
        count,
        selectedKeys: (factorInstances[key] || []).map((i) => i.key),
        serviceHistory,
        previousInstances: factorInstances[key] || []
      });
      normalizedInstances[key] = synced;
      if (synced.length >= 1) {
        const field = FACTOR_STATUS_FIELD[key];
        if (field) {
          statusFromInstances[field] = rollupInstanceStatuses(synced);
        }
      }
    });

    const pricingSnapshot = {
      ...pricing,
      factorValues,
      factorInstances: normalizedInstances,
      pfBalance: pfBalanceForPricing,
      finalAgreedFee: fees,
      initialAdvance: parseFloat(initialAdvance) || 0
    };

    return {
      ...(claim || {}),
      ...statusFromInstances,
      member_id: member?.id || claim?.member_id,
      requirements,
      requirement: requirements.join(', '),
      pf_balance: totalPfEpfBalance,
      eps_balance: totalEpsBalance,
      total_balance: totalPfEpfBalance + totalEpsBalance,
      fees,
      advance: totalPayments,
      payments,
      balance: fees - totalPayments,
      service_history: sortServiceHistory(serviceHistory.map((row) => normalizeServiceHistoryEntry(row))),
      pricing_data: pricingSnapshot,
      remarks,
      final_status: finalStatus,
      status: finalStatus,
      updated_at: new Date().toISOString()
    };
  };

  /** Persist current wizard data without closing; flips footer to Next */
  const handleStepSave = async () => {
    if (step === 1 && !validateFactorInstances()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (!payload.member_id) {
        toast.error('Member is required');
        return;
      }

      if (!claimRecordId) {
        const created = await claimsAPI.create(payload);
        const newId = created?.id;
        if (!newId) {
          toast.error('Claim saved but no id returned');
          return;
        }
        setClaimRecordId(newId);
        toast.success(`${STEPS[step].label} saved`);
      } else {
        await claimsAPI.update(claimRecordId, payload);
        toast.success(`${STEPS[step].label} saved`);
      }

      setStepNeedsSave(false);
      onSuccess?.();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save this step');
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    setStepNeedsSave(false);
  };

  const goBack = () => {
    setStep((s) => Math.max(0, s - 1));
    setStepNeedsSave(false);
  };

  const handleSubmit = async () => {
    if (!validateFactorInstances()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (!claimRecordId && isCreate) {
        if (!payload.member_id) {
          toast.error('Member is required');
          return;
        }
        await claimsAPI.create(payload);
        toast.success('Claim created successfully');
      } else {
        const id = claimRecordId || claim?.id;
        if (!id) {
          toast.error('Claim id missing');
          return;
        }
        await claimsAPI.update(id, payload);
        toast.success('Claim updated successfully');
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(isCreate ? 'Failed to create claim' : 'Failed to update claim');
    } finally {
      setSaving(false);
    }
  };

  const handleWhatsApp = () => {
    if (!member?.whatsapp) {
      toast.error('WhatsApp link not available for this member');
      return;
    }
    const updatedClaim = {
      ...(claim || {}),
      ...statuses,
      requirements,
      requirement: requirements.join(', '),
      status: finalStatus,
      fees: parseFloat(finalAgreedFee) || 0
    };
    const url = sendWhatsAppMessage(member.phone_number, updatedClaim, member);
    if (url) window.open(url, '_blank');
    else toast.error('Unable to generate WhatsApp message');
  };

  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const balanceDue = (parseFloat(finalAgreedFee) || 0) - totalPaid;

  const memberAge = useMemo(() => {
    const raw = member?.date_of_birth;
    if (!raw) return null;
    const dob = new Date(String(raw).slice(0, 10));
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age >= 0 && age < 150 ? age : null;
  }, [member?.date_of_birth]);

  const totalServiceLabel = useMemo(
    () => formatTotalServiceLabel(serviceHistory),
    [serviceHistory]
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-4">
      <div
        className="bg-white rounded-xl shadow-2xl flex overflow-hidden"
        style={{ width: MODAL_WIDTH, height: MODAL_HEIGHT }}
      >
        <aside className="hidden md:flex w-52 shrink-0 bg-gradient-to-b from-primary-100 via-primary-50 to-violet-50 text-slate-800 flex-col border-r border-primary-200">
          <div className="px-4 py-4 border-b border-primary-200/80">
            <div className="text-[10px] uppercase tracking-widest text-primary-600 font-semibold">
              {isCreate ? 'Add Claim' : 'Update Claim'}
            </div>
            <div className="text-sm font-semibold mt-1 leading-snug text-slate-900">{member?.name || 'Member'}</div>
          </div>
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {STEPS.map((s, idx) => (
          <button
                key={s.id}
                type="button"
                onClick={() => {
                  setStep(idx);
                  setStepNeedsSave(false);
                }}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition ${
                  step === idx
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-slate-700 hover:bg-primary-100/80'
                }`}
              >
                <span className="text-[10px] opacity-70 mr-1.5">{idx + 1}.</span>
                {s.label}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-primary-200/80 text-xs text-slate-500">
            Overall status: <span className="text-slate-800 font-medium">{finalStatus}</span>
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="grid grid-cols-3 items-center gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 shrink-0">
            <div className="min-w-0 justify-self-start">
              <h3 className="text-lg font-semibold text-slate-900">{STEPS[step].label}</h3>
              <p className="text-xs text-slate-500 mt-0.5 md:hidden">
                Step {step + 1} of {STEPS.length}
              </p>
            </div>
            <div className="justify-self-center text-center">
              <div className="inline-flex flex-wrap items-center justify-center gap-2">
                {memberAge != null ? (
                  <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-800">
                    Age {memberAge}
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">Age —</span>
                )}
                {totalServiceLabel ? (
                  <span
                    className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800"
                    title="Sum of all employments as years.months (months 0–11) — for pension eligibility"
                  >
                    Total service {totalServiceLabel}
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">Total service —</span>
                )}
              </div>
            </div>
            <div className="justify-self-end">
              <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5">
            {step === 0 && (
              <div className="flex flex-col h-full min-h-[420px]">
                <div className="sticky top-0 z-10 -mx-1 px-1 pb-3 bg-white flex justify-between items-center gap-2">
                  <p className="text-sm text-slate-600">Employment / PF account history.</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowImportHistory(true)}
                      className="btn-secondary inline-flex items-center text-sm shadow-sm"
                    >
                      <Upload className="w-4 h-4 mr-1" /> Import
                    </button>
                      <button
                        type="button"
                      onClick={() => {
                        markStepDirty();
                        setServiceHistory((p) => [...p, blankHistory()]);
                      }}
                      className="btn-secondary inline-flex items-center text-sm shadow-sm"
                      >
                      <Plus className="w-4 h-4 mr-1" /> Add
                      </button>
                  </div>
                  </div>
                {serviceHistory.length === 0 ? (
                  <p className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-lg">
                    No service history yet. Click Add.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {serviceHistory.map((row, index) => (
                      <div key={index} className="rounded-lg border border-slate-200 p-3 relative">
                <button
                  type="button"
                          onClick={() => {
                            markStepDirty();
                            setServiceHistory((p) => p.filter((_, i) => i !== index));
                          }}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                >
                          <Trash2 className="w-4 h-4" />
                </button>
                        <div className="text-xs font-medium text-slate-500 mb-2">Entry {index + 1}</div>
                        <div className="grid grid-cols-7 gap-2">
                          <Field
                            label="Company"
                            className="col-span-2"
                            value={row.company_name}
                            onChange={(v) => handleServiceHistoryChange(index, 'company_name', v)}
                            onBlur={() => handleCompanyBlur(index)}
                          />
                          <Field
                            label="Member ID"
                            className="col-span-2"
                            inputClassName="font-mono tracking-wide"
                            value={row.member_id}
                            onChange={(v) => handleServiceHistoryChange(index, 'member_id', v)}
                          />
                          <Field label="DOJ" type="date" value={row.doj} onChange={(v) => handleServiceHistoryChange(index, 'doj', v)} />
                          <Field label="DOL" type="date" value={row.dol} onChange={(v) => handleServiceHistoryChange(index, 'dol', v)} />
                          <Field label="Service" value={row.service || ''} readOnly />
                          <Field label="PF/EPF" type="number" value={row.pf_epf_balance} onChange={(v) => handleServiceHistoryChange(index, 'pf_epf_balance', v)} />
                          <Field label="EPS" type="number" value={row.eps_balance} onChange={(v) => handleServiceHistoryChange(index, 'eps_balance', v)} />
                          <Field label="Form 31" value={row.form_31} onChange={(v) => handleServiceHistoryChange(index, 'form_31', v)} />
                          <div>
                            <label className="field-label">Form 19</label>
                            <select
                              value={row.form_19 || 'No'}
                              onChange={(e) => handleServiceHistoryChange(index, 'form_19', e.target.value)}
                              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                            >
                              <option>No</option>
                              <option>Yes</option>
                            </select>
                          </div>
                          <div>
                            <label className="field-label">Form 10C</label>
                            <select
                              value={row.form_10c_scheme_certificate || 'No'}
                              onChange={(e) => handleServiceHistoryChange(index, 'form_10c_scheme_certificate', e.target.value)}
                              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                            >
                              <option>No</option>
                              <option>Yes</option>
                            </select>
                          </div>
                          <div>
                            <label className="field-label">Form-13</label>
                            <select
                              value={row.form_13 || 'NA'}
                              onChange={(e) => handleServiceHistoryChange(index, 'form_13', e.target.value)}
                              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                              title="No = transfer still needed (auto-fills Transfers count)"
                            >
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                              <option value="NA">NA</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Enter numeric values for each factor (0 = not in scope).
                  <strong> Transfers (No.)</strong> is auto-filled from Form-13 = No (not editable) and those employments are used for status / brief / escalate.
                  For JD Issues, Exit Date, EPS Merging, or Form-31 with count > 1, use <strong>Select IDs</strong>.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {CASE_FACTORS.map((factor) => {
                    const count = Math.max(0, parseFloat(factorValues[factor.key]) || 0);
                    const isTransfers = factor.key === 'transfers';
                    const needsInstances = !isTransfers && isInstanceTrackedFactor(factor.key) && count > 1;
                    const selectedCount = (factorInstances[factor.key] || []).length;
                    const transferMapped = (factorInstances.transfers || []);

                    return (
                      <div
                        key={factor.key}
                        className="rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{factor.label}</div>
                          {canUsePricing && (
                            <div className="text-[11px] text-slate-500">
                              Score {factor.scoreWeight} · Our {factor.ourDays}d · Wait {factor.waitDays}d
                            </div>
                          )}
                          {isTransfers && transferMapped.length > 0 && (
                            <div className="text-[11px] text-emerald-700 mt-0.5 truncate" title={transferMapped.map((i) => i.label).join(', ')}>
                              Auto: {transferMapped.map((i) => i.label || i.member_id).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {needsInstances && (
                            <button
                              type="button"
                              onClick={() => setInstancePickerFactorKey(factor.key)}
                              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-medium ${
                                selectedCount === count
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                                  : 'border-primary-300 bg-primary-50 text-primary-800'
                              }`}
                              title="Select Service History IDs to track"
                            >
                              <ListChecks className="w-3.5 h-3.5" />
                              Select IDs ({selectedCount}/{count})
                            </button>
                          )}
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={factorValues[factor.key] || 0}
                            readOnly={isTransfers}
                            onChange={(e) => setFactor(factor.key, e.target.value)}
                            className={`w-20 rounded-md border px-2 py-1.5 text-sm text-right shrink-0 ${
                              isTransfers
                                ? 'border-slate-200 bg-slate-50 text-slate-700 cursor-not-allowed'
                                : 'border-slate-300'
                            }`}
                            title={isTransfers ? 'Auto from Form-13 = No (not editable)' : undefined}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label className="field-label">Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => { markStepDirty(); setRemarks(e.target.value); }}
                    rows={2}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Internal notes…"
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                {canUsePricing && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Metric label="Score" value={pricing.totalScore} />
                    <Metric label="Suggested fee" value={`₹${pricing.suggestedFee.toLocaleString('en-IN')}`} />
                    <Metric label="List fee" value={`₹${pricing.listFee.toLocaleString('en-IN')}`} />
                    <Metric
                      label="Timeline"
                      value={pricing.timelineLabel}
                      sub={`${pricing.totalWorkingDays} WD · our ${pricing.timeline.ourDaysTotal} + wait ${pricing.timeline.maxExternalWait} + buffer ${pricing.timeline.bufferDays}`}
                    />
                  </div>
                )}

                {!canUsePricing && (
                  <p className="text-xs text-slate-500 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    Fee & timeline auto-calculator is available to Super Admin only. Enter agreed fee and payments below.
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="field-label">Final Agreed Fee (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={finalAgreedFee}
                      onChange={(e) => { markStepDirty(); setFinalAgreedFee(e.target.value); }}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Discount vs list: ₹{pricing.discount.toLocaleString('en-IN')} ({pricing.discountPercent}%)
                    </p>
                  </div>
                  <div>
                    <label className="field-label">Initial Advance (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={initialAdvance}
                      onChange={(e) => {
                        markStepDirty();
                        setAdvanceManual(true);
                        setInitialAdvance(e.target.value);
                      }}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Default {pricing.advancePercent}% of agreed
                      {advanceManual && (
                        <button type="button" className="ml-2 text-primary-600" onClick={() => setAdvanceManual(false)}>
                          Reset default
                        </button>
                      )}
                    </p>
                  </div>
                </div>
                
                <div>
                  <div className="sticky top-0 z-10 bg-white pb-2 flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-slate-800">Payments received</h4>
                  <button
                    type="button"
                      onClick={() => {
                        markStepDirty();
                        setPayments((p) => [...p, { amount: '', date: new Date().toISOString().split('T')[0] }]);
                      }}
                      className="btn-secondary inline-flex items-center text-sm shrink-0"
                  >
                      <Plus className="w-4 h-4 mr-1" /> Add payment
                  </button>
                </div>
                  {payments.length === 0 ? (
                    <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg py-6 text-center">No payments logged.</p>
                  ) : (
                    <div className="space-y-2">
                      {payments.map((payment, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-2">
                              <input
                                type="date"
                                value={payment.date || ''}
                            onChange={(e) => {
                              markStepDirty();
                              const next = [...payments];
                              next[index] = { ...next[index], date: e.target.value };
                              setPayments(next);
                            }}
                            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                          />
                              <input
                                type="number"
                            min="0"
                            placeholder="Amount"
                                value={payment.amount || ''}
                            onChange={(e) => {
                              markStepDirty();
                              const next = [...payments];
                              next[index] = { ...next[index], amount: e.target.value };
                              setPayments(next);
                            }}
                            className="w-36 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              markStepDirty();
                              setPayments((p) => p.filter((_, i) => i !== index));
                            }}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 text-sm flex flex-wrap gap-4">
                    <span>Paid: <strong>₹{totalPaid.toLocaleString('en-IN')}</strong></span>
                    <span>Balance: <strong className={balanceDue > 0 ? 'text-amber-700' : 'text-green-700'}>₹{balanceDue.toLocaleString('en-IN')}</strong></span>
                  </div>
                  </div>
                </div>
              )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 p-4 space-y-2 text-sm">
                  <Row label="Member" value={member?.name} />
                  <Row label="Activities" value={requirements.join(', ') || '—'} />
                  {canUsePricing && (
                    <Row label="Score / List fee" value={`${pricing.totalScore} / ₹${pricing.listFee.toLocaleString('en-IN')}`} />
                  )}
                  <Row label="Final agreed" value={`₹${(parseFloat(finalAgreedFee) || 0).toLocaleString('en-IN')}`} />
                  <Row label="Advance" value={`₹${(parseFloat(initialAdvance) || 0).toLocaleString('en-IN')}`} />
                  {canUsePricing && <Row label="Timeline" value={pricing.timelineLabel} />}
                  <Row label="Status" value={finalStatus} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {canUsePricing && (
                    <button type="button" onClick={() => setShowQuote(true)} className="btn-secondary inline-flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Quote
                    </button>
                  )}
                  <button type="button" onClick={handleWhatsApp} className="btn-secondary">
                    WhatsApp Update
                  </button>
                </div>
              </div>
            )}
        </div>
        
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50 shrink-0">
            <div className="justify-self-start">
              <button
                type="button"
                disabled={step === 0}
                onClick={goBack}
                className="btn-secondary inline-flex items-center disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
            </div>

            <div className="justify-self-center flex flex-wrap items-center justify-center gap-2">
              {step === 0 && (
                <>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-primary-400 text-primary-800 bg-transparent"
                    title={`Contribution ${formatInr(contributionBalances.pfContribution)} − Claimed (Form-19 Yes) ${formatInr(contributionBalances.pfClaimed)}`}
                  >
                    PF/EPF Balance: {formatInr(contributionBalances.pfBalance)}
                  </span>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-violet-400 text-violet-800 bg-transparent"
                    title={`Contribution ${formatInr(contributionBalances.epsContribution)} − Claimed (Form-10C Yes) ${formatInr(contributionBalances.epsClaimed)}`}
                  >
                    EPS Balance: {formatInr(contributionBalances.epsBalance)}
                  </span>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-emerald-500 text-emerald-800 bg-transparent"
                    title="PF/EPF Balance + EPS Balance"
                  >
                    Total Balance: {formatInr(contributionBalances.totalBalance)}
                  </span>
                </>
              )}
            </div>

            <div className="justify-self-end flex gap-2">
              {step < STEPS.length - 1 ? (
                stepNeedsSave ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleStepSave}
                    className="btn-primary inline-flex items-center disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving…' : `Save ${STEPS[step].label}`}
                  </button>
                ) : (
                  <button type="button" onClick={goNext} className="btn-primary inline-flex items-center">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                )
              ) : (
                <button type="button" disabled={saving} onClick={handleSubmit} className="btn-primary inline-flex items-center disabled:opacity-50">
                  <Save className="w-4 h-4 mr-2" />
                  {isCreate && !claimRecordId ? 'Create Claim' : 'Save Claim'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showImportHistory && (
        <ServiceHistoryImportModal
          onClose={() => setShowImportHistory(false)}
          onImport={(rows, { mode }) => {
            markStepDirty();
            setServiceHistory((prev) => {
              const incoming = (rows || []).map((r) => normalizeServiceHistoryEntry(r));
              return sortServiceHistory(mode === 'replace' ? incoming : [...prev, ...incoming]);
            });
          }}
        />
      )}

      {instancePickerFactorKey && (() => {
        const factor = CASE_FACTORS.find((f) => f.key === instancePickerFactorKey);
        const maxCount = Math.max(0, parseFloat(factorValues[instancePickerFactorKey]) || 0);
        const selected = factorInstances[instancePickerFactorKey] || [];
        const selectedKeys = new Set(selected.map((i) => i.key));
        return (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-3 sm:p-5">
            <div
              className="bg-white rounded-xl shadow-2xl w-full flex flex-col"
              style={{ width: 'min(960px, 96vw)', height: 'min(640px, 90vh)' }}
            >
              <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Select Service History for {factor?.label || 'tracking'}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Multi-select {maxCount} employment row(s) to track individually
                    {' '}({selected.length}/{maxCount} selected)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setInstancePickerFactorKey(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-auto min-h-0 p-4">
                {serviceHistory.length === 0 ? (
                  <p className="text-sm text-amber-700 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    No Service History rows yet. Go back to Service History and add employments first.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2.5 w-10"> </th>
                          <th className="px-3 py-2.5">Company</th>
                          <th className="px-3 py-2.5">Member ID</th>
                          <th className="px-3 py-2.5">DOJ</th>
                          <th className="px-3 py-2.5">DOL</th>
                          <th className="px-3 py-2.5">Service</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceHistory.map((row, index) => {
                          const rowKey = serviceHistoryRowKey(row, index);
                          const checked = selectedKeys.has(rowKey);
                          const disabled = !checked && selected.length >= maxCount;
                          return (
                            <tr
                              key={rowKey}
                              className={`border-t border-slate-100 ${checked ? 'bg-primary-50/60' : 'bg-white'} ${
                                disabled ? 'opacity-50' : 'hover:bg-slate-50 cursor-pointer'
                              }`}
                              onClick={() => {
                                if (disabled) return;
                                handleToggleFactorInstance(instancePickerFactorKey, rowKey);
                              }}
                            >
                              <td className="px-3 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() => handleToggleFactorInstance(instancePickerFactorKey, rowKey)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="rounded border-slate-300"
                                />
                              </td>
                              <td className="px-3 py-2.5 text-slate-900">
                                {row.company_name || '—'}
                              </td>
                              <td className="px-3 py-2.5 font-mono text-slate-700">
                                {row.member_id || '—'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700">{row.doj || '—'}</td>
                              <td className="px-3 py-2.5 text-slate-700">{row.dol || '—'}</td>
                              <td className="px-3 py-2.5 text-slate-700">{row.service || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 shrink-0 bg-slate-50/80">
                <p className="text-xs text-slate-500">
                  {selected.length === maxCount
                    ? 'Selection complete.'
                    : `Select ${maxCount - selected.length} more row(s).`}
                </p>
                <button
                  type="button"
                  onClick={() => setInstancePickerFactorKey(null)}
                  className="btn-primary"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showQuote && (
        <QuotePreview
          member={member}
          claim={claim || { id: 'NEW' }}
          pricing={{
            ...pricing,
            pfBalance: pfBalanceForPricing,
            finalAgreedFee: parseFloat(finalAgreedFee) || 0,
            initialAdvance: parseFloat(initialAdvance) || 0
          }}
          onClose={() => setShowQuote(false)}
        />
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, onBlur, type = 'text', readOnly, className = '', inputClassName = '' }) => (
  <div className={className}>
    <label className="field-label">{label}</label>
    <input
      type={type}
      value={value || ''}
      readOnly={readOnly}
      title={value || undefined}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      onBlur={onBlur}
      className={`w-full rounded-md border px-2 py-1.5 text-sm ${readOnly ? 'border-slate-200 bg-slate-50' : 'border-slate-300'} ${inputClassName}`}
    />
  </div>
);

const Metric = ({ label, value, sub }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-1 leading-snug">{sub}</div>}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex gap-3 justify-between">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="font-medium text-slate-900 text-right">{value}</span>
  </div>
);

export default UpdateClaimStatus;
